/**
 * ── US ADDRESSES: THE CHECK, AND THE SUGGESTIONS ─────────────────────────
 *
 * Shared by the builder's recipients step (assets/configurator.js) and the
 * cart's address editor (assets/cart-recipients.js), because an address typed
 * in one place and an address typed in the other must be judged by the same
 * rules. Two copies of "is this a real ZIP" is two answers.
 *
 * ⛔ WHY THIS EXISTS AT ALL. The recipient form validated SHAPE — any two
 * letters passed as a state, any five digits passed as a ZIP — and on
 * 2026-09-08 Daniel's own Shop Pay test produced the proof:
 *
 *     דניאל דואק · רבי תנחומא 9 דירה 12 · ירושלים, CA 93313
 *
 * A Jerusalem street wearing a California state code and a Bakersfield ZIP.
 * It passed everything. The delivery profile covers one zone — United States —
 * so that card could never have been fulfilled, and the envelope would have
 * printed a real Jerusalem street under a fake US state.
 *
 * ── WHAT IT CATCHES, AND WHAT IT DOES NOT ────────────────────────────────
 *
 * Catches, with no third party and no per-lookup cost:
 *   · non-Latin script in a name, street or city  → not a US address
 *   · a ZIP that is not a real US ZIP             → typo
 *   · a ZIP whose state is not the state given    → "New York, CA 10027"
 *
 * Does NOT catch, and cannot: a well-formed address nobody lives at. A street
 * that does not exist in that ZIP, a house number that does not exist, a flat
 * number that does not exist. Those need a verification service (CASS/DPV) and
 * cost money per lookup — see decisions.md 2026-09-08. Google's autocomplete
 * below narrows it a great deal by making the customer PICK a real address
 * rather than type one, but picking the wrong real address is still possible.
 *
 * ⚠️ THE CITY IS DELIBERATELY NOT CHECKED against the ZIP. It would need a
 * 41,000-row city table rather than the state table below, and it is the least
 * harmful of the three: USPS routes on the ZIP and the street, so a misspelt
 * city on an otherwise correct address still arrives. Non-Latin script in the
 * city — the case that actually happened — is caught by the script rule.
 *
 * ── THE DATA ─────────────────────────────────────────────────────────────
 * assets/us-zip-state.txt is every US ZIP and its state, from GeoNames
 * (CC BY 4.0), 40,979 of them, as fixed-width 7-character records — `12345MA`
 * — sorted, so a lookup is a binary search over a string with no parsing and
 * no allocation. 287 KB raw, ~87 KB over the wire, and fetched ONLY when an
 * address form is actually opened.
 *
 * ⚠️ It is a snapshot. A ZIP created after the file was built reads as
 * unknown, which shows the customer a warning on a valid address. Rare, and
 * the alternative — trusting any five digits — is what produced the line
 * above. Refresh the file if that ever becomes a real complaint.
 */
(function () {
  'use strict';

  var ZIP_URL = (window.PP_ZIP_URL || '');
  var REC = 7;                    // '12345MA'

  var zipTable = null;            // the raw string, once loaded
  var zipState = 'idle';          // idle | loading | ready | failed
  var waiters = [];

  /* ── THE WORLD, 2026-09-15 ─────────────────────────────────────────────
     We post internationally now (decisions.md 2026-09-15), so the Latin-script
     gate that refused a non-US address is GONE, by Daniel's call: "We don't
     need the check that tests that it's not an address outside the U.S."
     The US checks below stay — for a US address, and only for one. Outside
     the US nothing is checked beyond "filled in": we cannot validate the
     world, and pretending to is worse than not trying.

     ⚠️ THE LIST IS NO LONGER THE SHOPIFY MARKET'S — corrected 2026-09-25.
     It was, when it was written: every country the market would sell to. On
     2026-09-25 the shipping zone became an explicit 194-country allowlist
     that excludes the EEA, the UK and Switzerland, and this list kept all
     236. That is deliberate. See NO_SHIP below.

     Names come from the browser's Intl, so they are never a table to keep in
     step. */
  var COUNTRIES = ('US AF AX AL DZ AD AO AI AG AR AM AW AC AU AT AZ BS BH BD BB BY BE BZ BJ BM BT BO ' +
    'BA BW BR IO BN BG BF BI KH CA CV BQ KY CF TD CL CN CX CC CO KM CG CD CK CR HR CW CY CZ CI ' +
    'DK DJ DM DO EC EG SV GQ ER EE SZ ET FK FO FJ FI FR GF PF TF GA GM GE DE GH GI GR GL GD GP ' +
    'GT GG GN GW GY HT VA HN HK HU IS IN ID IQ IE IM IL IT JM JP JE JO KZ KE KI XK KW KG LA LV ' +
    'LB LS LR LY LI LT LU MO MG MW MY MV ML MT MQ MR MU YT MX MD MC MN ME MS MA MZ MM NA NR NP ' +
    'NL NC NZ NI NE NG NU NF MK NO OM PK PS PA PG PY PE PH PN PL PT QA CM RE RO RU RW BL SH KN ' +
    'LC MF PM WS SM ST SA SN RS SC SL SG SX SK SI SB SO ZA GS KR SS ES LK VC SD SR SJ SE CH TW ' +
    'TJ TZ TH TL TG TK TO TT TA TN TR TM TC TV UG UA AE GB UY UZ VU VE VN VG WF EH YE ZM ZW').split(' ');

  /* ── WHERE WE DO NOT POST, AND WHY IT IS NOT JUST A SHORTER LIST ───────
     The EEA, the UK and Switzerland. Not a logistics limit — a legal one:
     the privacy notice is written for US law and appoints no GDPR Article 27
     representatives, which only holds while we are not offering our services
     there. Full reasoning in the venture repo, projects/pop-cards/decisions.md,
     2026-09-25.

     ⛔ THESE STAY IN COUNTRIES, ON PURPOSE (Daniel, 2026-09-25). Deleting them
     from the dropdown would be the smaller change and the worse one: someone
     buying for their sister in Berlin would find the country simply absent and
     read it as a broken form. Naming the refusal is honest, and it is the only
     way the decision's reversal condition — real EEA or UK demand — ever gets
     evidence, which is what pp_country_blocked is for (configurator.js).

     The EU's outermost regions with their own codes are here because they are
     legally France and Finland: GF GP MQ RE YT MF AX. */
  var NO_SHIP = ('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE ' +
                 'IS LI NO GB CH GF GP MQ RE YT MF AX').split(' ');

  /* Intl gives bare names, which read wrong mid-sentence: "send cards to
     United Kingdom". Only for prose — NOT for countryLine(), because the
     envelope's last line is the postal convention (UNITED KINGDOM, no "the").
     The rule covers every name in NO_SHIP that needs it and generalises to
     the Philippines, the Bahamas, the Cayman Islands and the rest. */
  function countryPhrase(code) {
    var n = countryName(code);
    return (/^United |Islands$|^Netherlands$|^Philippines$|^Bahamas$|^Gambia$|^Maldives$|^Comoros$|^Seychelles$/.test(n))
      ? 'the ' + n : n;
  }

  /* "A", "A and B", "A, B and C". Intl.ListFormat does this properly where
     it exists; the fallback is the same shape, not a bare comma join. */
  var listFmt = null;
  try { listFmt = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }); } catch (e) { listFmt = null; }
  function listPhrase(parts) {
    parts = (parts || []).filter(Boolean);
    if (listFmt) { try { return listFmt.format(parts); } catch (e) { /* fall through */ } }
    if (parts.length < 2) return parts[0] || '';
    if (parts.length === 2) return parts[0] + ' and ' + parts[1];
    /* Oxford comma, because that is what Intl.ListFormat('en') produces and
       the two must not disagree depending on the browser. */
    return parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];
  }

  /* ⛔ ONE COPY OF THIS SENTENCE, AND THIS IS IT (2026-09-25). It used to be
     written out twice — configurator.js designShipsToLine() and
     cart-recipients.js shipsToLine() — under a comment reading "change both
     or neither, or the cart and the builder say it differently". Both callers
     already depend on PPAddress, so the comment's hazard is just deleted
     instead of being maintained.

     `only` is the design's shipsTo list (design-picker.js). */
  function shipsToLine(only) {
    only = only || [];
    /* No restriction is no sentence. Both callers guard already, but a
       function that can return "sent to ." is one refactor away from
       shipping it. */
    if (!only.length) return '';
    if (only.length === 1 && only[0] === 'US') {
      return 'This design can only be sent to addresses in the United States.';
    }
    return 'This design can only be sent to ' + listPhrase(only.map(countryPhrase)) + '.';
  }

  /** Can a card be posted to this country? */
  function shipsTo(code) {
    return NO_SHIP.indexOf(String(code || 'US').trim().toUpperCase()) === -1;
  }

  var names = null;
  try { names = new Intl.DisplayNames(['en'], { type: 'region' }); } catch (e) { names = null; }
  function countryName(code) {
    code = String(code || '').trim().toUpperCase();
    if (!code) return '';
    try { return (names && names.of(code)) || code; } catch (e) { return code; }
  }
  var options = null;
  /** [{ code, name }], the US first and the rest by name. Built once. */
  function countryOptions() {
    if (options) return options;
    var rest = COUNTRIES.slice(1).map(function (c) { return { code: c, name: countryName(c) }; })
      .sort(function (a, b) { return a.name.localeCompare(b.name, 'en'); });
    options = [{ code: 'US', name: countryName('US') }].concat(rest);
    return options;
  }
  function isUS(r) { return String((r && r.country) || 'US').trim().toUpperCase() === 'US'; }
  /** The envelope's last line: the country, full name, CAPITALS — and only
   *  abroad (print reference_geometry.json → envelope.international). */
  function countryLine(code) {
    code = String(code || 'US').trim().toUpperCase();
    return code === 'US' ? '' : countryName(code).toUpperCase();
  }

  var STATES = ('AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS ' +
                'MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI ' +
                'WY AS GU MP PR VI AA AE AP').split(' ');

  function loadZips() {
    if (zipState === 'ready' || zipState === 'failed') return Promise.resolve(zipState === 'ready');
    return new Promise(function (resolve) {
      waiters.push(resolve);
      if (zipState === 'loading') return;
      zipState = 'loading';
      if (!ZIP_URL) { finish(false); return; }
      fetch(ZIP_URL)
        .then(function (r) { if (!r.ok) throw new Error('zip_' + r.status); return r.text(); })
        .then(function (t) { zipTable = t; finish(t.length >= REC && t.length % REC === 0); })
        .catch(function () { finish(false); });
    });
    function finish(ok) {
      zipState = ok ? 'ready' : 'failed';
      /* ⛔ A FAILED LOAD MUST NOT BLOCK A REAL ADDRESS. If the file 404s or the
         customer is offline, every ZIP reads as unknown and we fall back to the
         format check alone. Refusing to sell because our lookup table did not
         download is a worse failure than an unverified ZIP. */
      var w = waiters; waiters = [];
      w.forEach(function (f) { f(ok); });
      if (typeof window.PPAddressReady === 'function') window.PPAddressReady(ok);
    }
  }

  /** The state for a ZIP: 'NY', or '' when the table has no such ZIP, or null
   *  when the table is not loaded and the question cannot be answered. */
  function stateForZip(zip) {
    if (zipState !== 'ready' || !zipTable) return null;
    var lo = 0, hi = zipTable.length / REC - 1;
    while (lo <= hi) {
      var mid = (lo + hi) >> 1;
      var at = zipTable.substr(mid * REC, 5);
      if (at === zip) return zipTable.substr(mid * REC + 5, 2);
      if (at < zip) lo = mid + 1; else hi = mid - 1;
    }
    return '';
  }

  function trim(s) { return String(s == null ? '' : s).trim(); }

  /**
   * Everything wrong with one address, in screen order, as
   * `[{ key, msg }]`. Empty means nothing detectable is wrong — NOT that the
   * address is real. See the header.
   *
   * ⛔ EMPTY FIELDS ARE NOT REPORTED HERE. "You have not typed the city yet"
   * and "that ZIP is not a US ZIP" are different sentences at different
   * moments, and mixing them tells someone their blank form is wrong.
   * Missing-ness is the caller's job (recipMissing / missing).
   */
  function problems(r) {
    r = r || {};
    var out = [];

    /* ⛔ FIRST, because no other fix matters until it is: a country we do not
       post to is a wrong value, said at once, exactly like a wrong ZIP. It
       lives here rather than in the builder so the cart's address editor
       (cart-recipients.js) gets it for nothing — both callers come through
       this function, and only this function. */
    var cc = String(r.country || 'US').trim().toUpperCase();
    if (cc && !shipsTo(cc)) {
      out.push({ key: 'country',
                 msg: 'We can’t send cards to ' + countryPhrase(cc) + ' yet.' });
      /* Nothing below applies: every blocked country is outside the US. */
      return out;
    }

    /* Outside the US there is nothing honest to check (see COUNTRIES). */
    if (!isUS(r)) return out;

    var state = trim(r.state).toUpperCase();
    var zip = trim(r.zip);

    var stateOk = !state || STATES.indexOf(state) !== -1;
    if (!stateOk) {
      out.push({ key: 'state', msg: '“' + state + '” isn’t a US state code.' });
    }

    if (zip && /^[0-9]{5}$/.test(zip)) {
      var real = stateForZip(zip);
      if (real === '') {
        out.push({ key: 'zip', msg: zip + ' isn’t a US ZIP code — check the digits.' });
      /* ⚠️ The mismatch is only worth saying when the state is a real one.
         "ZZ isn't a US state code" and "10027 is a NY ZIP code, not ZZ" are the
         same complaint twice, and the second reads as if ZZ were plausible. */
      } else if (real && state && stateOk && real !== state) {
        out.push({ key: 'zip',
                   msg: zip + ' is a ' + real + ' ZIP code, not ' + state + '.' });
      }
    }
    return out;
  }

  /* ═══ GOOGLE PLACES AUTOCOMPLETE ═══════════════════════════════════════
     Optional, and the form is fully usable without it. The key is a theme
     setting, so it is not in this file and not in git — see
     config/settings_schema.json, "Google Maps API key".

     ⛔ NO KEY, NO SUGGESTIONS, NO ERROR. An address form that works is the
     baseline; suggestions are an improvement on it. Everything below is
     wrapped so a missing key, a blocked script, a quota refusal or an API
     shape change costs the customer nothing but typing.

     ⭐ `includedRegionCodes` is the country the customer chose (2026-09-15;
     it was a fixed `['us']` while the US-only decision stood).

     Uses the HEADLESS Autocomplete Data API rather than the
     PlaceAutocompleteElement web component, because the component renders its
     own input and we would be fighting it for the field styling, the accent
     border and the Alpine binding. This way the input stays ours. */

  var mapsPromise = null;
  var places = null;            // the resolved places library

  /**
   * ⛔ `loading=async` MEANS THE LIBRARY IS NOT READY AT `onload`.
   *
   * This first shipped checking `google.maps.places.AutocompleteSuggestion`
   * inside `s.onload`, and it was false EVERY TIME — the script tag has loaded
   * by then, but the places module has not. The promise cached that `false`
   * for the life of the page, so every attach returned a no-op and the field
   * behaved exactly as if no key were set. Nothing errored. Nothing logged.
   * The only symptom was silence, and Daniel found it by typing an address
   * into a live form on Draft and watching nothing happen.
   *
   * `importLibrary` is the documented pairing for `loading=async` and is the
   * only thing that actually knows when the module has arrived. Measured on
   * Draft PP 2026-09-08: at `onload`, `google.maps.places` is undefined; three
   * seconds later it is fully present. So the old check was not merely fragile,
   * it was guaranteed to fail.
   */
  function loadMaps() {
    if (mapsPromise) return mapsPromise;
    var key = window.PP_GMAPS_KEY;
    if (!key) return (mapsPromise = Promise.resolve(false));
    mapsPromise = new Promise(function (resolve) {
      var done = false;
      var finish = function (lib) {
        if (done) return;
        done = true;
        clearTimeout(to);
        places = lib && lib.AutocompleteSuggestion ? lib : null;
        resolve(!!places);
      };
      var to = setTimeout(function () { finish(null); }, 10000);

      /* ⛔ THE CALLBACK, NOT `onload`, AND NOT A READINESS CHECK.
         Two attempts failed here before this one, both the same mistake in
         different clothes: asking "is it ready?" at a moment Google has not
         promised anything about.
           1. checked google.maps.places at onload — undefined, always.
           2. checked google.maps.importLibrary at onload — also not there yet.
         `callback=` is the one moment the API guarantees, and with
         `loading=async` it is the documented pairing. From inside it,
         importLibrary resolves the module we actually want. Nothing here
         guesses. */
      var cbName = 'PPMapsInit';
      window[cbName] = function () {
        try {
          google.maps.importLibrary('places').then(finish, function () { finish(null); });
        } catch (e) { finish(null); }
      };

      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
              '&libraries=places&loading=async&v=weekly&callback=' + cbName;
      s.onerror = function () { finish(null); };
      document.head.appendChild(s);
    });
    return mapsPromise;
  }

  /* Google's own component maps an address to `addressComponents`; this pulls
     out the seven fields our form holds. `street_number` and `route` are
     separate components and have to be joined — in that order, which is the US
     convention and not the convention everywhere. Good enough for a street
     line abroad; the customer can correct it in the field. */
  function fieldsFromPlace(place) {
    var by = {};
    (place.addressComponents || []).forEach(function (c) {
      (c.types || []).forEach(function (t) { if (!by[t]) by[t] = c; });
    });
    var g = function (t, form) {
      var c = by[t];
      if (!c) return '';
      return (form === 'short' ? (c.shortText || c.short_name) : (c.longText || c.long_name)) || '';
    };
    var num = g('street_number');
    var route = g('route');
    return {
      line1: [num, route].filter(Boolean).join(' '),
      line2: g('subpremise'),
      city: g('locality') || g('sublocality') || g('postal_town') || g('neighborhood'),
      state: g('administrative_area_level_1', 'short'),
      zip: g('postal_code'),
      country: g('country', 'short')
    };
  }

  /**
   * Turn one text input into an address field with suggestions.
   *
   * @param input  the street-address <input>
   * @param onPick called with { line1, line2, city, state, zip } when a
   *               suggestion is chosen. The caller writes the fields — this
   *               module never touches anything but its own input, because in
   *               the builder those fields belong to Alpine and writing them
   *               behind its back is the "don't write styles a directive owns"
   *               trap wearing a different hat.
   * @returns a teardown function.
   */
  function attach(input, onPick, opts) {
    opts = opts || {};
    var list = null, items = [], active = -1, token = null, seq = 0, dead = false;

    /* ⛔ TAKING THE LIST OFF THE PAGE AND FORGETTING WHAT WAS IN IT ARE TWO
       DIFFERENT JOBS, AND CONFLATING THEM COST AN EVENING.

       `close()` did both, and `render()` opened by calling it — so every
       render cleared `items` and then returned early because `items` was
       empty. The dropdown could never appear, under any circumstances. The
       API call fired, Google answered with five suggestions, and the handler
       threw them away one line later.

       It looked exactly like a Google problem from the outside: no error, no
       log, no list. It was not. */
    function removeList() {
      if (list) { list.remove(); list = null; }
      active = -1;
    }

    function close() {
      removeList();
      items = [];
    }

    function render() {
      removeList();
      if (!items.length) return;
      list = document.createElement('ul');
      list.className = 'pp-aclist';
      list.setAttribute('role', 'listbox');
      items.forEach(function (it, i) {
        var li = document.createElement('li');
        li.className = 'pp-acitem';
        li.setAttribute('role', 'option');
        li.textContent = it.text;
        /* ⛔ POINTERDOWN, NOT ONLY MOUSEDOWN (2026-10-09). On an iPhone the mouse events of a tap arrive late, after
           the field's blur has closed the list, so a tapped suggestion did nothing (iPhone Safari, the Simulator).
           pointerdown comes first, on every device. The list also sits inside the field's <label>, so the click is
           kept from reaching it (a label click refocuses the field). choose() ignores a second call. */
        li.addEventListener('pointerdown', function (e) { e.preventDefault(); choose(i); });
        li.addEventListener('mousedown', function (e) { e.preventDefault(); choose(i); });
        li.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); });
        list.appendChild(li);
      });
      /* Positioned by the caller's CSS relative to the field's wrapper. The
         label element around our inputs is already `position: relative`. */
      (input.parentNode || document.body).appendChild(list);
    }

    function mark() {
      if (!list) return;
      [].forEach.call(list.children, function (li, i) {
        li.classList.toggle('is-active', i === active);
      });
    }

    function choose(i) {
      var it = items[i];
      if (!it) return;
      close();
      Promise.resolve(it.place.fetchFields({ fields: ['addressComponents'] }))
        .then(function () {
          /* The session ends with the details call — that is what makes the
             keystrokes free under Google's session pricing. A fresh token per
             address, minted on the next keystroke. */
          token = null;
          var f = fieldsFromPlace(it.place);
          if (f.line1) input.value = f.line1;
          try { onPick(f); } catch (e) { /* the caller's problem, not ours */ }
        })
        .catch(function () { /* leave what they typed; it is still an address */ });
    }

    function query() {
      var text = input.value.trim();
      if (text.length < MIN_CHARS) { close(); return; }
      var mine = ++seq;
      /* From the library `importLibrary` handed back, not the global
         namespace — the global is populated eventually, but `places` is the
         object we actually waited for and know is ready. */
      if (!token) token = new places.AutocompleteSessionToken();
      places.AutocompleteSuggestion
        .fetchAutocompleteSuggestions({
          input: text,
          sessionToken: token,
          /* ⭐ PINNED TO THE COUNTRY THE CUSTOMER CHOSE, read at query time so
             changing the select re-aims the next keystroke. Pinned rather than
             open because the street is joined US-style (number, route) and a
             pick from the wrong country would overwrite the select's answer.
             ⛔ NO `language`: pinning it relabels a US address in that
             language, and that label goes on an envelope (plan 1.2, measured). */
          includedRegionCodes: [String((opts.country && opts.country()) || 'US').toLowerCase()],
          includedPrimaryTypes: ['street_address', 'premise', 'subpremise']
        })
        .then(function (res) {
          if (dead || mine !== seq || document.activeElement !== input) return;
          items = (res.suggestions || []).slice(0, 5).map(function (s) {
            var p = s.placePrediction;
            return { text: p.text && p.text.toString ? p.text.toString() : String(p.text),
                     place: p.toPlace() };
          });
          render();
        })
        .catch(function () { close(); });
    }

    var debounce = null;
    function onInput() {
      clearTimeout(debounce);
      debounce = setTimeout(query, DEBOUNCE_MS);
    }
    function onKey(e) {
      if (!list) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(active + 1, items.length - 1); mark(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(active - 1, 0); mark(); }
      else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(active); }
      /* ⛔ AND THE KEY STOPS HERE. Escape with the suggestions open means "close the suggestions", nothing more; let
         through, it reached the builder's window handler, which left the whole "Who's it for?" step for the card
         (2026-10-09, the builder B plan's Escape fix, Pop List builder-audit-bugs). `list` is null when closed, so a
         second Escape still reaches the page. */
      else if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); close(); }
    }

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKey);
    input.addEventListener('blur', function () { setTimeout(close, 120); });
    input.setAttribute('autocomplete', 'off');   // ours, not the browser's

    /* ⛔ CATCH UP ON WHAT WAS TYPED BEFORE WE EXISTED. This is the bug Yonatan
       reported as "the autocomplete doesn't find the addresses I give it"
       (2026-09-10), and it is not about Google's matching at all.

       attachAutocomplete() waits for loadMaps() before calling attach(), and
       this file's own measurement says the library takes ABOUT THREE SECONDS to
       become usable. Until then the field has no 'input' listener. Type an
       address inside that window and stop — which is what typing an address is
       — and no further input event ever fires, so nothing is ever queried. The
       field looks broken while working exactly as written.

       Warming (see warm()) makes the window rare. This makes it harmless: if
       there is already something in the box when we attach, query it now
       instead of waiting for a keystroke that may never come. */
    if (input.value.trim().length >= MIN_CHARS) query();

    return function teardown() {
      dead = true;
      clearTimeout(debounce);
      close();
      input.removeEventListener('input', onInput);
      input.removeEventListener('keydown', onKey);
    };
  }

  /* ── HOW SOON SUGGESTIONS APPEAR ────────────────────────────────────────
     Daniel, 2026-09-10: "let's warm it up so it'll be immediate upon type."
     Three separate costs used to stack, and only one of them was the debounce:

       the library    ~3s from first request to usable  -> warm() below
       MIN_CHARS      nothing happened under 3 characters
       DEBOUNCE_MS    180ms after every keystroke

     ⚠️ TWO, NOT ONE. A single character of a street address matches nothing
     useful and spends a request to prove it; two is where a prefix starts
     meaning something. Session tokens are what keep the extra keystrokes from
     being billed one at a time — do not remove them while tuning this. */
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 80;

  /** Start fetching Google Maps NOW, so the field is live before anyone
   *  reaches it. Idempotent — loadMaps() memoises — and a no-op with no key,
   *  so it is safe to call from anywhere and safe to call repeatedly.
   *  ⚠️ Call it EARLIER than the address form, or it buys nothing: the form
   *  opening is already what triggers the load today. */
  function warm() { return loadMaps(); }

  /** Attach if — and only if — Google is configured and loads. Safe to call
   *  on every form; it resolves to a no-op teardown when there is no key. */
  function attachAutocomplete(input, onPick, opts) {
    return loadMaps().then(function (ok) {
      if (!ok || !input) return function () {};
      return attach(input, onPick, opts);
    });
  }

  window.PPAddress = {
    countryOptions: countryOptions,
    countryName: countryName,
    countryLine: countryLine,
    /** countryName, but reading correctly inside a sentence. Prose only. */
    countryPhrase: countryPhrase,
    /** The one copy of the design-restriction sentence. Both UIs use it. */
    shipsToLine: shipsToLine,
    /** State and a 5-digit ZIP are required for a US address only. */
    isUS: isUS,
    /** False for the EEA, the UK and Switzerland — see NO_SHIP. */
    shipsTo: shipsTo,
    loadZips: loadZips,
    problems: problems,
    stateForZip: stateForZip,
    attachAutocomplete: attachAutocomplete,
    warm: warm,
    hasSuggestions: function () { return !!window.PP_GMAPS_KEY; },
    zipsReady: function () { return zipState === 'ready'; }
  };
})();
